import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import socketService from '../services/socketService';
import firebaseNotificationService from '../services/firebaseNotificationService';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [pushNotifications, setPushNotifications] = useState([]);
  
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  
  const isMountedRef = useRef(true);
  const listenersRegisteredRef = useRef(false);

  useEffect(() => {
    const loadNotifications = () => {
      try {
        const savedNotifications = localStorage.getItem('agent_notifications');
        if (savedNotifications) {
          const parsedNotifications = JSON.parse(savedNotifications);
          console.log('AGENT - Loaded socket notifications from localStorage:', parsedNotifications.length);
          setNotifications(parsedNotifications);
        }
        
        const savedPushNotifications = firebaseNotificationService.getStoredNotifications();
        console.log('AGENT - Loaded push notifications:', savedPushNotifications.length);
        setPushNotifications(savedPushNotifications);
        
      } catch (error) {
        console.error('AGENT - Failed to load notifications from localStorage:', error);
        localStorage.removeItem('agent_notifications');
      }
    };

    loadNotifications();
  }, []);

  useEffect(() => {
    const saveNotifications = () => {
      try {
        localStorage.setItem('agent_notifications', JSON.stringify(notifications));
        console.log('AGENT - Saved socket notifications to localStorage:', notifications.length);
      } catch (error) {
        console.error('AGENT - Failed to save notifications to localStorage:', error);
      }
    };

    if (notifications.length > 0) {
      saveNotifications();
    }
  }, [notifications]);

  useEffect(() => {
    const handlePushNotification = (event) => {
      console.log('AGENT - Push notification event received:', event.detail);
      
      const pushNotification = {
        id: event.detail.data?.notificationId || `push_${Date.now()}`,
        title: event.detail.notification?.title || 'Push Notification',
        description: event.detail.notification?.body || 'You have a new notification',
        type: event.detail.data?.type || 'info',
        from: event.detail.data?.from || 'System',
        createdAt: new Date().toISOString(),
        isRead: false,
        data: event.detail.data,
        source: 'push'
      };
      
      setPushNotifications(prev => {
        const exists = prev.some(notif => notif.id === pushNotification.id);
        if (exists) {
          console.log('🔄 AGENT - Push notification already exists, skipping');
          return prev;
        }
        return [pushNotification, ...prev.slice(0, 99)];
      });
    };
    
    window.addEventListener('agent-firebase-message', handlePushNotification);
    window.addEventListener('agent-new-push-notification', handlePushNotification);
    
    return () => {
      window.removeEventListener('agent-firebase-message', handlePushNotification);
      window.removeEventListener('agent-new-push-notification', handlePushNotification);
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    
    console.log('AGENT - SocketProvider useEffect - Auth status:', {
      isAuthenticated,
      userId: user?.id,
      userType: user?.user_type
    });

    if (isAuthenticated && user && user.id) {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('AGENT - No token available for socket connection');
        return;
      }

      const socket = socketService.connect(token);

      if (!listenersRegisteredRef.current) {
        console.log('AGENT - Registering socket event listeners for agent');
        
        const handleSystemNotification = (notification) => {
          if (!isMountedRef.current) return;
          
          console.log('AGENT - SOCKET NOTIFICATION RECEIVED:', {
            id: notification.id,
            title: notification.title,
            timestamp: new Date().toISOString()
          });
          
          setNotifications(prev => {
            const exists = prev.some(notif => notif.id === notification.id);
            if (exists) {
              console.log('AGENT - Socket notification already exists, skipping duplicate');
              return prev;
            }
            
            const newNotifications = [notification, ...prev.slice(0, 99)];
            console.log('AGENT - Socket notifications count after add:', newNotifications.length);
            return newNotifications;
          });
        };

        const handleOnlineUsers = (users) => {
          if (!isMountedRef.current) return;
          console.log('AGENT - Online users updated:', users.length);
          setOnlineUsers(users);
        };

        const handleProductCreated = (notification) => {
          if (!isMountedRef.current) return;
          console.log('AGENT - Product created notification:', notification);
          setNotifications(prev => {
            const exists = prev.some(notif => notif.id === notification.id);
            if (exists) return prev;
            return [notification, ...prev.slice(0, 99)];
          });
        };

        const handleOrderNotification = (notification) => {
          if (!isMountedRef.current) return;
          console.log('AGENT - Order notification received:', notification);
          setNotifications(prev => {
            const exists = prev.some(notif => notif.id === notification.id);
            if (exists) return prev;
            return [notification, ...prev.slice(0, 99)];
          });
        };

        socketService.onSystemNotification(handleSystemNotification);
        socketService.onOnlineUsers(handleOnlineUsers);
        socketService.onProductCreated(handleProductCreated);
        socketService.onOrderNotification(handleOrderNotification);
        
        listenersRegisteredRef.current = true;

        socket.on('connect', () => {
          if (!isMountedRef.current) return;
          console.log('AGENT - Socket connected in agent context! ID:', socket.id);
          setIsConnected(true);
          
          setTimeout(() => {
            socketService.registerUser({
              userId: user.id,
              role: user?.agentDetail?.accountType,
              username: user.username,
              email: user.email,
              type: 'agent'
            });
          }, 500);
        });

        socket.on('disconnect', (reason) => {
          if (!isMountedRef.current) return;
          console.log('AGENT - Socket disconnected in agent context:', reason);
          setIsConnected(false);
        });

        socket.on('connect_error', (error) => {
          if (!isMountedRef.current) return;
          console.error('AGENT - Socket connection error in agent context:', error.message);
          setIsConnected(false);
        });
      }

      return () => {
        console.log('AGENT - Cleaning up agent socket context');
        isMountedRef.current = false;
        listenersRegisteredRef.current = false;
        
        if (socketService.getConnectionStatus()) {
          socketService.disconnect();
        }
      };
    } else {
      console.log('AGENT - Socket connection skipped - missing auth:', {
        isAuthenticated,
        hasUser: !!user,
        hasUserId: user?.id
      });

      if (socketService.getConnectionStatus()) {
        socketService.disconnect();
        setIsConnected(false);
      }
    }
  }, [isAuthenticated, user]);

  const allNotifications = [...notifications, ...pushNotifications].sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  );

  const unreadCount = allNotifications.filter(n => !n.isRead).length;

  const markNotificationAsRead = (notificationId) => {
    const socketNotification = notifications.find(n => n.id === notificationId);
    if (socketNotification) {
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
    }
    
    const pushNotification = pushNotifications.find(n => n.id === notificationId);
    if (pushNotification) {
      firebaseNotificationService.markNotificationAsRead(notificationId);
      setPushNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
    }
  };

  const clearNotifications = () => {
    setNotifications([]);
    firebaseNotificationService.clearAllNotifications();
    setPushNotifications([]);
    localStorage.removeItem('agent_notifications');
  };

  const value = {
    isConnected,
    onlineUsers,
    notifications: allNotifications,
    markNotificationAsRead,
    clearNotifications,
    unreadCount
  };

  console.log('🔌 AGENT - SocketProvider render - State:', {
    isConnected,
    onlineUsersCount: onlineUsers.length,
    socketNotifications: notifications.length,
    pushNotifications: pushNotifications.length,
    totalNotifications: allNotifications.length,
    unreadCount
  });

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};